import React, {useState} from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Autocomplete,
} from "@mui/material";
import {BadgeCheck, Building2, Cable, Info, Key, Pencil, Plus, Search, Server, Trash2, X} from "lucide-react";
import {CREDENCIAIS_SECRET_PATTERN, apiHostDoAmbiente, getBankAccountStatusPalette} from "../financeiroUtils";

const createInitialValues = (account) => ({
    apelido: account?.apelido || '',
    status: account?.status || 'ATIVA',
    banco: account?.banco || '',
    bancoNomeComercial: account?.bancoNomeComercial || '',
    codigoBanco: account?.codigoBanco || '',
    agencia: account?.agencia || '',
    agenciaDv: account?.agenciaDv || '',
    conta: account?.conta || '',
    contaDv: account?.contaDv || '',
    tipoConta: account?.tipoConta || 'CONTA_CORRENTE',
    ambiente: account?.ambiente || 'HOMOLOGACAO',
    // Só o nome do cofre do Secrets Manager (BBF-139). A credencial do banco nunca passa por aqui.
    credenciaisSecret: account?.credenciaisSecret || '',
    convenio: account?.convenio || '',
    carteira: account?.carteira || '',
    variacaoCarteira: account?.variacaoCarteira || '',
    ultimoNossoNumero: Number(account?.ultimoNossoNumero || 0),
    protestoAutomatico: Boolean(account?.protestoAutomatico),
    protestoDias: Number(account?.protestoDias || 30),
    aceitaAposVencimento: account?.aceitaAposVencimento ?? true,
    diasAposVencimento: Number(account?.diasAposVencimento || 30),
    percentualJurosAtraso: Number(account?.percentualJurosAtraso || 0),
    percentualMultaAtraso: Number(account?.percentualMultaAtraso || 0),
    aceitaPagamentoParcial: Boolean(account?.aceitaPagamentoParcial),
    valorMinimoParcial: Number(account?.valorMinimoParcial || 50),
    gerarQrcodePix: account?.gerarQrcodePix ?? true,
});

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: '#fff',
    },
};

const sectionTitleSx = {fontSize: '1rem', color: '#374151', fontWeight: 600};

const getBankOptionLabel = (option) => {
    if (!option) return '';
    const name = option.nomeComercial || option.nome || '';
    const code = option.codigo || '';
    if (!name && !code) return option.id || '';
    return `${name || '-'} - ${code || '-'}`;
};

function BankAccountDialog({
    account,
    open,
    saving,
    bankOptions,
    bankOptionsLoading,
    onChangeBankOptionsSearch,
    onClose,
    onSave,
    onTestConnection,
}) {
    const [values, setValues] = useState(createInitialValues(account));
    const [bankInputValue, setBankInputValue] = useState('');
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        const nextValues = createInitialValues(account);
        setValues(nextValues);
        setBankInputValue(getBankOptionLabel({
            id: nextValues.banco,
            nomeComercial: nextValues.bancoNomeComercial,
            codigo: nextValues.codigoBanco,
        }));
        setErrors({});
    }, [account, open]);

    const handleChange = (field) => (event) => {
        const nextValue = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setValues((current) => ({...current, [field]: nextValue}));
    };

    const validate = () => {
        const nextErrors = {};

        if (!values.apelido.trim()) nextErrors.apelido = 'Nome da conta é obrigatório';
        if (!values.banco) nextErrors.banco = 'Banco é obrigatório';
        if (!values.agencia.trim()) nextErrors.agencia = 'Agência é obrigatória';
        if (!values.conta.trim()) nextErrors.conta = 'Conta é obrigatória';
        if (!values.convenio.trim()) nextErrors.convenio = 'Convênio é obrigatório';
        if (values.credenciaisSecret.trim() && !CREDENCIAIS_SECRET_PATTERN.test(values.credenciaisSecret.trim())) {
            nextErrors.credenciaisSecret = 'Informe só o nome do cofre, como busifarm/dev/financeiro/conectores/bb-cobranca. Nunca a credencial.';
        }
        if (values.aceitaAposVencimento) {
            if (Number(values.percentualMultaAtraso) < 0) nextErrors.percentualMultaAtraso = 'A multa não pode ser negativa';
            if (Number(values.percentualJurosAtraso) < 0) nextErrors.percentualJurosAtraso = 'Os juros não podem ser negativos';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const selectedBank = bankOptions.find((item) => item.id === values.banco)
        || (values.banco ? {
            id: values.banco,
            nomeComercial: values.bancoNomeComercial,
            codigo: values.codigoBanco,
        } : null);

    const handleSubmit = async () => {
        if (!validate()) return;
        const success = await onSave?.(values, account?.id);
        if (success) onClose?.();
    };

    const renderSwitchBlock = (field, label, children = null) => (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                <Switch checked={Boolean(values[field])} onChange={handleChange(field)} />
                <Typography sx={{fontSize: 14, color: '#374151'}}>{label}</Typography>
            </Box>
            {values[field] ? children : null}
        </Box>
    );

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{sx: {borderRadius: 3, maxHeight: '90vh'}}}>
            <DialogTitle sx={{p: 0}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1}}>
                    <Box>
                        <Typography sx={{fontSize: '1.5rem', color: '#111827', fontWeight: 700}}>
                            {account ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                            Configure os dados da conta e parametrização para emissão de boletos
                        </Typography>
                    </Box>
                    <Button onClick={onClose} disabled={saving} sx={{minWidth: 0, p: 1, borderRadius: 2, color: '#6b7280'}}>
                        <X size={18} />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{p: 3}}>
                <Stack spacing={4}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Building2 size={18} />
                            <Typography sx={sectionTitleSx}>Informações da Conta</Typography>
                        </Stack>

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2}}>
                            <TextField
                                label="Nome da Conta"
                                value={values.apelido}
                                onChange={handleChange('apelido')}
                                error={Boolean(errors.apelido)}
                                helperText={errors.apelido}
                                placeholder="Ex: Conta Corrente Principal"
                                sx={{...inputSx, gridColumn: {md: 'span 2'}}}
                            />

                            <Autocomplete
                                options={bankOptions}
                                value={selectedBank}
                                inputValue={bankInputValue}
                                loading={bankOptionsLoading}
                                getOptionLabel={getBankOptionLabel}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onInputChange={(_, value, reason) => {
                                    setBankInputValue(value);
                                    if (reason === 'input') onChangeBankOptionsSearch?.(value);
                                    if (reason === 'clear') {
                                        onChangeBankOptionsSearch?.('');
                                        setValues((current) => ({
                                            ...current,
                                            banco: '',
                                            bancoNomeComercial: '',
                                            codigoBanco: '',
                                        }));
                                    }
                                }}
                                onChange={(_, option) => {
                                    setBankInputValue(getBankOptionLabel(option));
                                    setValues((current) => ({
                                        ...current,
                                        banco: option?.id || '',
                                        bancoNomeComercial: option?.nomeComercial || '',
                                        codigoBanco: option?.codigo || '',
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Banco"
                                        placeholder="Busque pelo nome comercial do banco"
                                        error={Boolean(errors.banco)}
                                        helperText={errors.banco || 'Selecione o banco cadastrado para relacionar a conta.'}
                                        sx={inputSx}
                                    />
                                )}
                            />

                            <TextField
                                label="Agência"
                                value={values.agencia}
                                onChange={handleChange('agencia')}
                                error={Boolean(errors.agencia)}
                                helperText={errors.agencia}
                                placeholder="1234"
                                sx={inputSx}
                            />

                            <TextField
                                label="Conta"
                                value={values.conta}
                                onChange={handleChange('conta')}
                                error={Boolean(errors.conta)}
                                helperText={errors.conta}
                                placeholder="12345-6"
                                sx={inputSx}
                            />

                            <TextField
                                label="Convênio"
                                value={values.convenio}
                                onChange={handleChange('convenio')}
                                error={Boolean(errors.convenio)}
                                helperText={errors.convenio}
                                placeholder="3123456"
                                sx={inputSx}
                            />
                        </Box>

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'}, gap: 2, mt: 2}}>
                            <TextField label="Carteira" value={values.carteira} onChange={handleChange('carteira')} placeholder="17" sx={inputSx} />
                            <TextField label="Variação da Carteira" value={values.variacaoCarteira} onChange={handleChange('variacaoCarteira')} placeholder="35" sx={inputSx} />
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, border: '1px solid #d1d5db', borderRadius: 2, bgcolor: '#fff'}}>
                                <Switch checked={values.status === 'ATIVA'} onChange={(event) => setValues((current) => ({...current, status: event.target.checked ? 'ATIVA' : 'INATIVA'}))} />
                                <Typography sx={{fontSize: 14, color: '#374151'}}>Conta Ativa</Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{pt: 3, borderTop: '1px solid #e5e7eb'}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Server size={18} />
                            <Typography sx={sectionTitleSx}>Configuração da API</Typography>
                        </Stack>

                        <Box sx={{p: 2, borderRadius: 2, border: '1px solid #bfdbfe', bgcolor: '#eff6ff', mb: 2.5}}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Info size={18} color="#2563eb" style={{marginTop: 2, flexShrink: 0}} />
                                <Box>
                                    <Typography sx={{fontSize: 14, color: '#1d4ed8', fontWeight: 700, mb: 0.5}}>Integração Banco do Brasil</Typography>
                                    <Typography variant="body2" sx={{color: '#2563eb'}}>
                                        A credencial do banco (client_id, client_secret e chave de aplicação) fica num cofre do AWS Secrets Manager e nunca passa pelo backoffice. Aqui vai só o nome do cofre. Os endereços da API são os oficiais do banco para o ambiente escolhido.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack spacing={2}>
                            <TextField
                                select
                                label="Ambiente"
                                value={values.ambiente}
                                onChange={handleChange('ambiente')}
                                helperText={`API usada: ${apiHostDoAmbiente(values.ambiente)}`}
                                sx={inputSx}
                            >
                                <MenuItem value="HOMOLOGACAO">Homologação</MenuItem>
                                <MenuItem value="PRODUCAO">Produção</MenuItem>
                            </TextField>
                            <TextField
                                label="Cofre da credencial (Secrets Manager)"
                                value={values.credenciaisSecret}
                                onChange={handleChange('credenciaisSecret')}
                                error={Boolean(errors.credenciaisSecret)}
                                helperText={errors.credenciaisSecret || 'Só o nome do cofre, ex.: busifarm/dev/financeiro/conectores/bb-cobranca. O valor nunca aparece aqui.'}
                                placeholder="busifarm/dev/financeiro/conectores/bb-cobranca"
                                inputProps={{autoComplete: 'off', spellCheck: false}}
                                sx={inputSx}
                            />
                        </Stack>
                    </Box>

                    <Box sx={{pt: 3, borderTop: '1px solid #e5e7eb'}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Key size={18} />
                            <Typography sx={sectionTitleSx}>Parametrização de Boletos</Typography>
                        </Stack>

                        <Box sx={{p: 2, borderRadius: 2, border: '1px solid #fde68a', bgcolor: '#fefce8', mb: 2.5}}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Info size={18} color="#ca8a04" style={{marginTop: 2, flexShrink: 0}} />
                                <Box>
                                    <Typography sx={{fontSize: 14, color: '#a16207', fontWeight: 700, mb: 0.5}}>Regras de Emissão e Cobrança</Typography>
                                    <Typography variant="body2" sx={{color: '#a16207'}}>
                                        Configure as políticas de protesto, aceitação de pagamento e controle sequencial.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack spacing={3}>
                            <Box>
                                <TextField
                                    label="Último Nosso Número Emitido"
                                    type="number"
                                    value={values.ultimoNossoNumero}
                                    onChange={handleChange('ultimoNossoNumero')}
                                    fullWidth
                                    sx={inputSx}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                    Controle sequencial automático. O próximo boleto será: {Number(values.ultimoNossoNumero || 0) + 1}
                                </Typography>
                            </Box>

                            {renderSwitchBlock('protestoAutomatico', 'Protesto Automático', (
                                <Box>
                                    <TextField
                                        label="Dias após vencimento para protesto"
                                        type="number"
                                        value={values.protestoDias}
                                        onChange={handleChange('protestoDias')}
                                        fullWidth
                                        sx={inputSx}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                        Mínimo: 3 dias | Recomendado: 30 dias
                                    </Typography>
                                </Box>
                            ))}

                            {renderSwitchBlock('aceitaAposVencimento', 'Aceita Pagamento Após Vencimento', (
                                <Box>
                                    <TextField
                                        label="Aceitar até quantos dias após o vencimento?"
                                        type="number"
                                        value={values.diasAposVencimento}
                                        onChange={handleChange('diasAposVencimento')}
                                        fullWidth
                                        sx={inputSx}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                        Após esse período, o boleto não poderá mais ser pago
                                    </Typography>
                                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2, mt: 2}}>
                                        <TextField
                                            label="Multa por atraso (%)"
                                            type="number"
                                            value={values.percentualMultaAtraso}
                                            onChange={handleChange('percentualMultaAtraso')}
                                            error={Boolean(errors.percentualMultaAtraso)}
                                            helperText={errors.percentualMultaAtraso || 'Cobrada uma vez, no dia seguinte ao vencimento'}
                                            inputProps={{min: 0, step: 0.01}}
                                            sx={inputSx}
                                        />
                                        <TextField
                                            label="Juros ao mês (%)"
                                            type="number"
                                            value={values.percentualJurosAtraso}
                                            onChange={handleChange('percentualJurosAtraso')}
                                            error={Boolean(errors.percentualJurosAtraso)}
                                            helperText={errors.percentualJurosAtraso || 'Proporcional aos dias de atraso'}
                                            inputProps={{min: 0, step: 0.01}}
                                            sx={inputSx}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                        Vão para o boleto registrado no banco. Zero dispensa.
                                    </Typography>
                                </Box>
                            ))}

                            {renderSwitchBlock('aceitaPagamentoParcial', 'Aceita Pagamento Parcial', (
                                <Box>
                                    <TextField
                                        label="Valor Mínimo para Pagamento Parcial (%)"
                                        type="number"
                                        value={values.valorMinimoParcial}
                                        onChange={handleChange('valorMinimoParcial')}
                                        fullWidth
                                        sx={inputSx}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                        Percentual mínimo do valor total que deve ser pago
                                    </Typography>
                                </Box>
                            ))}

                            {renderSwitchBlock('gerarQrcodePix', 'Gerar QR Code PIX')}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{display: 'flex', justifyContent: 'space-between', px: 3, py: 2.5, borderTop: '1px solid #e5e7eb'}}>
                <Box>
                    {account?.id ? (
                        <Button onClick={() => onTestConnection?.(account.id)} disabled={saving} startIcon={<Cable size={16} />}>
                            Testar conexão
                        </Button>
                    ) : null}
                </Box>

                <Stack direction="row" spacing={1.5}>
                    <Button onClick={onClose} disabled={saving} sx={{border: '1px solid #d1d5db', color: '#374151'}}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                        {account ? 'Salvar Alterações' : 'Cadastrar Conta'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

export default function FinanceiroBankAccountsTab({
    items,
    pagination,
    loading,
    saving,
    filter,
    search,
    page,
    bankOptions,
    bankOptionsLoading,
    onChangeFilter,
    onChangeSearch,
    onChangePage,
    onChangeBankOptionsSearch,
    onSave,
    onDelete,
    onTestConnection,
}) {
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const openDialog = (account = null) => {
        setEditing(account);
        setDialogOpen(true);
    };

    return (
        <Stack spacing={3}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    value={search}
                    onChange={(event) => onChangeSearch?.(event.target.value)}
                    placeholder="Buscar por apelido, banco ou conta..."
                    fullWidth
                    InputProps={{startAdornment: <Search size={18} color="#9ca3af" style={{marginRight: 8}} />}}
                />
                <TextField select value={filter || 'TODAS'} onChange={(event) => onChangeFilter?.(event.target.value === 'TODAS' ? '' : event.target.value)} sx={{minWidth: 190}}>
                    <MenuItem value="TODAS">Todos os status</MenuItem>
                    <MenuItem value="ATIVA">ATIVA</MenuItem>
                    <MenuItem value="INATIVA">INATIVA</MenuItem>
                    <MenuItem value="BLOQUEADA">BLOQUEADA</MenuItem>
                </TextField>
                <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => openDialog()} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}, whiteSpace: 'nowrap'}}>
                    Nova conta bancária
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                <Table>
                    <TableHead sx={{bgcolor: '#f9fafb'}}>
                        <TableRow>
                            <TableCell>Conta</TableCell>
                            <TableCell>Agência / Conta</TableCell>
                            <TableCell>Convênio</TableCell>
                            <TableCell>Boletos</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items.length ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                        Nenhuma conta bancária encontrada.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : items.map((item) => {
                            const palette = getBankAccountStatusPalette(item.status);
                            return (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{width: 40, height: 40, borderRadius: 2, bgcolor: '#ccfbf1', color: '#0f766e', display: 'grid', placeItems: 'center'}}>
                                                <Building2 size={20} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{color: '#111827', fontWeight: 600}}>{item.apelido || '-'}</Typography>
                                                <Typography variant="body2" color="text.secondary">{item.bancoNomeComercial || item.banco || '-'} • {item.codigoBanco || '-'}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{color: '#111827'}}>{item.agencia}{item.agenciaDv ? `-${item.agenciaDv}` : ''}</Typography>
                                        <Typography variant="body2" color="text.secondary">{item.conta}{item.contaDv ? `-${item.contaDv}` : ''}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{color: '#111827'}}>{item.convenio || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">Carteira {item.carteira || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{color: '#134e4a', fontWeight: 600}}>{String(item.ultimoNossoNumero || 0).padStart(11, '0')}</Typography>
                                        <Typography variant="body2" color="text.secondary">{item.ambiente || '-'} • PIX {item.gerarQrcodePix ? 'habilitado' : 'desabilitado'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.status} size="small" sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button variant="outlined" startIcon={<BadgeCheck size={16} />} onClick={() => onTestConnection?.(item.id)} disabled={saving}>Testar</Button>
                                            <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => openDialog(item)}>Editar</Button>
                                            <Button color="error" variant="outlined" startIcon={<Trash2 size={16} />} onClick={() => onDelete?.(item.id)} disabled={saving}>Excluir</Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Mostrando {(Number(page || 0) * 10) + (items.length ? 1 : 0)} a {Math.min((Number(page || 0) * 10) + items.length, pagination?.total || items.length)} de {pagination?.total || items.length} contas
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={!pagination?.has_previous} onClick={() => onChangePage?.(Math.max(0, Number(page || 0) - 1))} sx={{minWidth: 44, px: 1.5}}>
                        Anterior
                    </Button>
                    <Button variant="outlined" disabled={!pagination?.has_next} onClick={() => onChangePage?.(Number(page || 0) + 1)} sx={{minWidth: 44, px: 1.5}}>
                        Próxima
                    </Button>
                </Stack>
            </Stack>

            <BankAccountDialog
                open={dialogOpen}
                account={editing}
                saving={saving || loading}
                bankOptions={bankOptions}
                bankOptionsLoading={bankOptionsLoading}
                onChangeBankOptionsSearch={onChangeBankOptionsSearch}
                onClose={() => setDialogOpen(false)}
                onSave={onSave}
                onTestConnection={onTestConnection}
            />
        </Stack>
    );
}
